/**
 * PATHS — one per pair of neighbouring regions, routed over the path of least resistance.
 *
 * Each region's PATH NODE is its feature point (RegionField.cellCentre — the anchor everything else
 * in the region is already built from), and every region is joined to its east and its south
 * neighbour, so each adjacent pair gets exactly one path and all of a region's paths meet at its
 * node. The route is found by A* on an 8 m lattice where a step costs its length times
 * (1 + grade²·weight) and water is forbidden — so paths run straight over flat land, hug contours
 * along hillsides, SWITCHBACK up steep ground (the gently-climbing diagonal steps are genuinely the
 * cheapest way up), and go round the coast rather than across the water. No RNG is involved in
 * routing: the trace is a pure function of the terrain, so client and server agree by construction.
 *
 * The raw route is chained 8 m lattice steps. Collinear runs are merged, the corners are cut twice
 * (Chaikin), and the result is a polyline whose bends are gentler than the ~4 m ribbon drawn around
 * it, so nothing angular survives at a visible scale. The LONGITUDINAL PROFILE — terrain height at
 * each vertex, box-blurred along the line with the ENDPOINTS PINNED to the terrain so every path
 * arriving at a node agrees about its height — is what the path's surface follows: it pitches with
 * the land and ignores the metre-scale bumps.
 *
 * WHY THIS IS CHEAP, which is the design's first constraint (sampleHeight is the hottest function
 * in the project and the height blend lives inside it):
 *   - Tracing happens ONCE PER REGION PAIR, lazily, memoised — a few hundred lattice points on easy
 *     ground, hard-capped at MAX_EXPANSIONS on hard ground, each point's height sampled once.
 *   - Per column, the question "am I near a path?" is ONE presence-grid read (a 32 m cell keyed to
 *     the segments that reach it). Columns away from every path — almost all of them — pay that one
 *     read and nothing else. Near a path it is a handful of point-segment distance tests, pure
 *     arithmetic, no noise anywhere in the system.
 *   - `ensureAt` memoises per region cell, so the lazy tracing costs a Set hit once warm.
 *
 * Nothing here imports the generator: heights, sea level and node positions come in as plain
 * functions/numbers, so the module is testable alone and cannot recurse into the height blend.
 */
import { VOXEL_SCALE } from '../../voxel/constants.js';
import { hashInt2 } from '../../util/rng.js';
import { mat } from '../../materials/index.js';
import { packPairKey, unpackPairKey } from '../../util/packedKey.js';
export const DEFAULT_PATHS_CONFIG = {
    enabled: true,
    halfWidthM: 1.5,
    apronM: 4,
    gradeWeight: 30,
    wanderM: 150,
};
/** What a path's surface is made of — one per path, picked by the pair's hash. */
const PATH_MATERIALS = ['pebbles', 'dirt', 'cobble', 'cobble2', 'brick8'];
const MATERIAL_SALT = 0x9a7405;
/** A* lattice pitch, metres. Coarse on purpose: the cost model is macroscopic, the smoothing is not. */
const STEP_M = 8;
/** The hard cap on A* — the worst mountain trace stops here and the pair honestly has no path. */
const MAX_EXPANSIONS = 4096;
/** Water clearance: a lattice point below sea + this much is not land enough to walk on. Metres. */
const SHORE_M = 2;
/** Chaikin corner-cutting passes over the simplified route. */
const CHAIKIN_PASSES = 2;
/** Box-blur passes over the longitudinal profile (radius 1, endpoints pinned). */
const PROFILE_BLUR_PASSES = 3;
/**
 * The steepest the DRAWN surface may pitch, as a grade. The router avoids steep ground at its own
 * 8 m resolution, but a cliff narrower than a lattice step can hide between two sampled points and
 * put a wall into the profile; the clamp (a forward and a backward sweep) cuts the bench through it
 * instead. 0.5 is already a scramble on foot.
 */
const MAX_PROFILE_GRADE = 0.5;
/** Presence-grid cell, metres. */
const GRID_M = 32;
/**
 * The bench's fade WIDENS WITH THE HEIGHT MISMATCH: fade = max(apronM, mismatch · APRON_SPREAD).
 * A fixed apron turns a big profile-terrain divergence (the grade clamp cutting through a cliff,
 * cut-and-fill across a steep side-slope) into a near-vertical trench or causeway wall: the whole
 * mismatch fades over a few metres. Spreading it proportionally caps the wall's grade at about
 * 1.5 / APRON_SPREAD (smoothstep's steepest point is 1.5), so a deep cut becomes a hillside.
 */
const APRON_SPREAD = 2;
/** Cap on the widened fade, metres — also how far past the ribbon a blend query must see. */
const MAX_BENCH_APRON_M = 48;
/** The height kernel's softening (metres²): ~the ribbon, so on-ribbon columns read their own leg. */
const Y_KERNEL_W0_SQ = 2.25;
/**
 * The coarsest stride that still draws the paving (LOD 6, 16 m voxels); LOD 7+ rings keep the bench
 * but drop the paint — past that a floored stripe would exaggerate a 3 m path past 32 m and drag
 * lazy tracing out to the horizon. Also sizes the grid registration below: `skinAt`'s margin is the
 * coarse cell's half-extent, so the largest margin a query can carry is half this stride in metres,
 * and with the width floor a paint match can sit up to 3x that from the centreline.
 */
export const PATH_SKIN_MAX_STRIDE = 64;
const SKIN_MARGIN_MAX_M = (PATH_SKIN_MAX_STRIDE * VOXEL_SCALE) / 2;
/** The eight lattice steps. */
const DIRS = [
    [1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1],
];
export class PathNetwork {
    cfg;
    deps;
    /** halfWidth + apron: past this a column is untouched. Metres. */
    reachM;
    /** Traced pairs: packPairKey(ci,cj)*2 + dir (0 = east, 1 = south) -> true. Value is unused —
     *  the segments live in the pool — but the key set is what makes tracing once-per-pair. */
    traced = new Set();
    /** Region cells whose every reachable pair is traced. The warm-path memo for ensureAt. */
    ensured = new Set();
    // The segment pool: one flat set of arrays for every path (perf guide: typed arrays, no objects).
    n = 0;
    ax = new Float64Array(256);
    az = new Float64Array(256);
    bx = new Float64Array(256);
    bz = new Float64Array(256);
    ay = new Float64Array(256);
    by = new Float64Array(256);
    smat = new Int32Array(256);
    /** 32 m presence grid: cell -> indices of segments whose ribbon+apron can reach it. */
    grid = new Map();
    /**
     * Routing-lattice heights, SHARED ACROSS ROUTES. A height is a full noise stack (the dominant
     * cost of tracing, measured), and adjacent pairs' wander boxes overlap heavily — a per-route memo
     * resampled the same lattice dozens of times across one ensure sweep. Capped like the generator's
     * column memo; heights are pure in the config, and any config change rebuilds the network.
     */
    latticeHeights = new Map();
    /**
     * ONE radius for both the grid registration and the height kernel's support — they MUST match.
     * The kernel averages over the segments in the query's cell, and a segment whose weight is
     * nonzero for a query must therefore be in that cell's list, or the average changes when the
     * query steps over a cell border — measured as a 32 m square lattice of height creases around
     * every path. So the kernel is truncated to exactly this radius (see kernelFloorW).
     */
    gridReachM;
    /** Subtracted from every kernel weight so it reaches zero AT gridReachM instead of near it. */
    kernelFloorW;
    constructor(cfg, deps) {
        this.cfg = { ...DEFAULT_PATHS_CONFIG, ...cfg };
        this.deps = deps;
        this.reachM = this.cfg.halfWidthM + this.cfg.apronM;
        // The farthest any query can match: a coarse skin cell at the stride cap, or a blend on the
        // widest possible fade.
        this.gridReachM = Math.max(this.reachM + 3 * SKIN_MARGIN_MAX_M, this.cfg.halfWidthM + MAX_BENCH_APRON_M);
        const atReach = 1 / (this.gridReachM * this.gridReachM + Y_KERNEL_W0_SQ);
        this.kernelFloorW = atReach * atReach;
    }
    get halfWidthM() { return this.cfg.halfWidthM; }
    /**
     * The height a column actually gets, given its raw terrain height — the bench. Full profile
     * inside the ribbon, smoothstep back to raw terrain across the apron. This is the one entry
     * point the generator's sampleHeight calls, so everything that reads heights agrees about paths.
     */
    blendHeight(worldX, worldZ, rawFine) {
        // The margin extends the query out to the widest possible fade — a column 40 m from a deep
        // mountain cut is still on that cut's hillside. Columns near no path answer as before: one
        // grid read, null.
        const s = this.sampleAt(worldX, worldZ, Math.max(0, MAX_BENCH_APRON_M - this.cfg.apronM));
        if (s === null)
            return rawFine;
        const { halfWidthM, apronM } = this.cfg;
        const diffM = Math.abs(SCRATCH.yFine - rawFine) * VOXEL_SCALE;
        const fadeM = Math.min(MAX_BENCH_APRON_M, Math.max(apronM, diffM * APRON_SPREAD));
        if (SCRATCH.distM >= halfWidthM + fadeM)
            return rawFine;
        const t = SCRATCH.distM <= halfWidthM ? 1
            : 1 - smoothstep((SCRATCH.distM - halfWidthM) / fadeM);
        return rawFine + (SCRATCH.yFine - rawFine) * t;
    }
    /** Is this position on the ribbon itself (skin / scatter suppression), within `marginM` extra? */
    onPath(worldX, worldZ, marginM = 0) {
        const s = this.sampleAt(worldX, worldZ);
        return s !== null && SCRATCH.distM <= this.cfg.halfWidthM + marginM;
    }
    /**
     * The ribbon's material at a position, or -1. `marginM` is the half-extent of the coarse cell the
     * sample stands for (0 at LOD 0): a cell is paved when the ribbon touches ANY of it — the same
     * area convention as the city's `isRoadCell` — so coarse paint holds one whole voxel wide instead
     * of dashing wherever a cell centre happens to miss a ribbon narrower than the cell.
     */
    skinAt(worldX, worldZ, marginM = 0) {
        // THE WIDTH FLOOR: the painted half-width never drops below one whole coarse cell (2·marginM).
        // A material is binary per voxel, so a ribbon narrower than the lattice rasterises as a stripe
        // exactly one voxel wide, and a one-voxel stripe tracking a smooth curve must step sideways in
        // whole-voxel jumps — the aliasing you see from a far ring. Flooring the band at ~2-3 voxels
        // keeps its interior solid, so the unavoidable steps stay edge detail instead of displacing the
        // whole stripe. Inert at LOD 0-2, where a cell is thinner than the configured half-width anyway.
        const halfW = Math.max(this.cfg.halfWidthM, 2 * marginM);
        const s = this.sampleAt(worldX, worldZ, halfW - this.cfg.halfWidthM + marginM);
        return s !== null && SCRATCH.distM <= halfW + marginM ? SCRATCH.material : -1;
    }
    /**
     * Nearest-path query. Returns the module-level SCRATCH (valid until the next call) or null when
     * nothing is within reach + marginM — the common case, answered by one grid read. `marginM` must
     * stay within 3x SKIN_MARGIN_MAX_M (skinAt's worst case: cell half-extent + width floor): that is
     * what the grid registration is inflated by, so one read of the query point's own cell is still
     * guaranteed to see every segment a margined query can hit.
     */
    sampleAt(worldX, worldZ, marginM = 0) {
        this.ensureAt(worldX, worldZ);
        const cell = this.grid.get(packPairKey(Math.floor(worldX / GRID_M), Math.floor(worldZ / GRID_M)));
        if (cell === undefined)
            return null;
        // Distance and material come from the NEAREST segment; the height is a kernel blend over every
        // reachable one. Nearest-only height is discontinuous on the inside of a bend — the Voronoi
        // boundary between an ascending and a descending leg flips yFine by whole metres in one step,
        // and the bench faithfully builds that crease as a wall (measured grade 1.8 on a 20 m ridge
        // crossing). The kernel is smooth everywhere and collapses to the nearest segment's own profile
        // wherever no other leg is close, so the bench still IS the profile on the ribbon.
        // The kernel falls as 1/d⁴, NOT 1/d²: the crease only needs metres of mixing, and a heavier
        // tail borrows the profile from tens of metres up and down the line — wherever the profile
        // curves (a crest between steep drops) the ribbon's edges sagged toward the neighbourhood
        // average and the path read as humped instead of flat across. And it is TRUNCATED at gridReachM
        // (the floor subtraction), or the answer depends on which far segments happen to be in this
        // cell's list and creases along every cell border.
        let best = Infinity, bestMat = 0, wSum = 0, wySum = 0;
        for (let i = 0; i < cell.length; i++) {
            const k = cell[i];
            const dx = this.bx[k] - this.ax[k], dz = this.bz[k] - this.az[k];
            const px = worldX - this.ax[k], pz = worldZ - this.az[k];
            const l2 = dx * dx + dz * dz;
            let t = l2 > 0 ? (px * dx + pz * dz) / l2 : 0;
            t = t < 0 ? 0 : t > 1 ? 1 : t;
            const ex = px - dx * t, ez = pz - dz * t;
            const d2 = ex * ex + ez * ez;
            const b = 1 / (d2 + Y_KERNEL_W0_SQ);
            const w = b * b - this.kernelFloorW;
            if (w > 0) {
                wSum += w;
                wySum += w * (this.ay[k] + (this.by[k] - this.ay[k]) * t);
            }
            if (d2 < best) {
                best = d2;
                bestMat = this.smat[k];
            }
        }
        best = Math.sqrt(best);
        if (best > this.reachM + marginM || wSum <= 0)
            return null;
        SCRATCH.distM = best;
        SCRATCH.yFine = wySum / wSum;
        SCRATCH.material = bestMat;
        return SCRATCH;
    }
    /**
     * Make sure every pair whose route could reach this position is traced. A route stays inside its
     * endpoints' bounding box + `wanderM`, and an endpoint jitters up to half a cell, so pairs whose
     * lattice cells are within `span` of this cell cover everything. Memoised per region cell: warm
     * cost is one Set hit.
     */
    ensureAt(worldX, worldZ) {
        const size = this.deps.cellSize;
        const ci = Math.floor(worldX / size), cj = Math.floor(worldZ / size);
        const key = packPairKey(ci, cj);
        if (this.ensured.has(key))
            return;
        this.ensured.add(key);
        const span = 1 + Math.ceil((this.cfg.wanderM + this.reachM) / size + 0.5);
        for (let j = cj - span; j <= cj + span; j++) {
            for (let i = ci - span; i <= ci + span; i++) {
                this.tracePair(i, j, 0);
                this.tracePair(i, j, 1);
            }
        }
    }
    /** Trace the path from cell (ci,cj) to its east (dir 0) or south (dir 1) neighbour, once ever. */
    tracePair(ci, cj, dir) {
        const key = packPairKey(ci, cj) * 2 + dir;
        if (this.traced.has(key))
            return;
        this.traced.add(key);
        const a = this.deps.cellCentre(ci, cj);
        const b = this.deps.cellCentre(ci + (dir === 0 ? 1 : 0), cj + (dir === 1 ? 1 : 0));
        const line = this.route(a.x, a.z, b.x, b.z);
        if (!line)
            return;
        const material = mat(PATH_MATERIALS[hashInt2(key & 0x7fffffff, MATERIAL_SALT) % PATH_MATERIALS.length]);
        this.register(line, material);
    }
    /**
     * A* between two points on the STEP_M lattice. Returns the smoothed polyline with its profile
     * as [x, z, yFine, x, z, yFine, ...], or null (endpoint in water, or budget exhausted).
     */
    route(x0, z0, x1, z1) {
        const { rawHeight, routeHeight, seaFine } = this.deps;
        const seaLimit = seaFine === -Infinity ? -Infinity : seaFine + SHORE_M / VOXEL_SCALE;
        const gw = this.cfg.gradeWeight;
        const sx = Math.round(x0 / STEP_M), sz = Math.round(z0 / STEP_M);
        const tx = Math.round(x1 / STEP_M), tz = Math.round(z1 / STEP_M);
        // The route box: endpoints + wander margin. Also what makes the worst case finite.
        const w = Math.ceil(this.cfg.wanderM / STEP_M);
        const loX = Math.min(sx, tx) - w, hiX = Math.max(sx, tx) + w;
        const loZ = Math.min(sz, tz) - w, hiZ = Math.max(sz, tz) + w;
        const heights = this.latticeHeights;
        if (heights.size > 262144)
            heights.clear();
        const hAt = (gx, gz) => {
            const k = packPairKey(gx, gz);
            let h = heights.get(k);
            if (h === undefined) {
                h = routeHeight(gx * STEP_M, gz * STEP_M);
                heights.set(k, h);
            }
            return h;
        };
        if (hAt(sx, sz) < seaLimit || hAt(tx, tz) < seaLimit)
            return null;
        // A*: gScore/cameFrom in Maps (a route touches a few thousand nodes at most), open set a
        // binary heap of (f, key) pairs, laziest-deletion style.
        const g = new Map();
        const came = new Map();
        const heapF = [];
        const heapK = [];
        const push = (f, k) => {
            let i = heapF.length;
            heapF.push(f);
            heapK.push(k);
            while (i > 0) {
                const p = (i - 1) >> 1;
                if (heapF[p] <= heapF[i])
                    break;
                const tf = heapF[p];
                heapF[p] = heapF[i];
                heapF[i] = tf;
                const tk = heapK[p];
                heapK[p] = heapK[i];
                heapK[i] = tk;
                i = p;
            }
        };
        const pop = () => {
            const k = heapK[0];
            const lf = heapF.pop();
            const lk = heapK.pop();
            if (heapF.length > 0) {
                heapF[0] = lf;
                heapK[0] = lk;
                let i = 0;
                for (;;) {
                    const l = 2 * i + 1, r = l + 1;
                    let m = i;
                    if (l < heapF.length && heapF[l] < heapF[m])
                        m = l;
                    if (r < heapF.length && heapF[r] < heapF[m])
                        m = r;
                    if (m === i)
                        break;
                    const tf = heapF[m];
                    heapF[m] = heapF[i];
                    heapF[i] = tf;
                    const tk = heapK[m];
                    heapK[m] = heapK[i];
                    heapK[i] = tk;
                    i = m;
                }
            }
            return k;
        };
        const hyp = (gx, gz) => Math.sqrt((gx - tx) ** 2 + (gz - tz) ** 2) * STEP_M;
        const startK = packPairKey(sx, sz);
        const goalK = packPairKey(tx, tz);
        g.set(startK, 0);
        push(hyp(sx, sz), startK);
        let pops = 0;
        let found = false;
        while (heapF.length > 0) {
            const cur = pop();
            if (cur === goalK) {
                found = true;
                break;
            }
            if (++pops > MAX_EXPANSIONS)
                break;
            const [cgx, cgz] = unpackPairKey(cur);
            const cg = g.get(cur);
            const ch = hAt(cgx, cgz);
            for (let d = 0; d < 8; d++) {
                const ngx = cgx + DIRS[d][0], ngz = cgz + DIRS[d][1];
                if (ngx < loX || ngx > hiX || ngz < loZ || ngz > hiZ)
                    continue;
                const nh = hAt(ngx, ngz);
                if (nh < seaLimit)
                    continue;
                const dist = d < 4 ? STEP_M : STEP_M * Math.SQRT2;
                const grade = Math.abs(nh - ch) * VOXEL_SCALE / dist;
                const cost = dist * (1 + gw * grade * grade);
                const nk = packPairKey(ngx, ngz);
                const ng = cg + cost;
                const old = g.get(nk);
                if (old !== undefined && old <= ng)
                    continue;
                g.set(nk, ng);
                came.set(nk, cur);
                push(ng + hyp(ngx, ngz), nk);
            }
        }
        if (!found)
            return null;
        // Reconstruct (goal -> start), then reverse into metres.
        const rev = [];
        for (let k = goalK; k !== undefined; k = came.get(k))
            rev.push(k);
        let px = [];
        let pz = [];
        for (let i = rev.length - 1; i >= 0; i--) {
            const [gx, gz] = unpackPairKey(rev[i]);
            px.push(gx * STEP_M);
            pz.push(gz * STEP_M);
        }
        // Simplify: merge collinear runs, so Chaikin rounds real corners instead of lattice joints.
        const kx = [px[0]];
        const kz = [pz[0]];
        for (let i = 1; i < px.length - 1; i++) {
            const d1x = px[i] - kx[kx.length - 1], d1z = pz[i] - kz[kz.length - 1];
            const d2x = px[i + 1] - px[i], d2z = pz[i + 1] - pz[i];
            if (d1x * d2z - d1z * d2x !== 0 || d1x * d2x + d1z * d2z < 0) {
                kx.push(px[i]);
                kz.push(pz[i]);
            }
        }
        kx.push(px[px.length - 1]);
        kz.push(pz[pz.length - 1]);
        px = kx;
        pz = kz;
        // Chaikin corner cutting: every corner becomes two quarter-points; endpoints stay put.
        for (let pass = 0; pass < CHAIKIN_PASSES; pass++) {
            const nx = [px[0]];
            const nz = [pz[0]];
            for (let i = 0; i < px.length - 1; i++) {
                nx.push(px[i] * 0.75 + px[i + 1] * 0.25);
                nz.push(pz[i] * 0.75 + pz[i + 1] * 0.25);
                nx.push(px[i] * 0.25 + px[i + 1] * 0.75);
                nz.push(pz[i] * 0.25 + pz[i + 1] * 0.75);
            }
            nx.push(px[px.length - 1]);
            nz.push(pz[pz.length - 1]);
            px = nx;
            pz = nz;
        }
        // The longitudinal profile: terrain height per vertex, box-blurred along the line with the
        // ENDPOINTS PINNED, so every path arriving at a region's node agrees about its height there.
        const m = px.length;
        let prof = new Float64Array(m);
        for (let i = 0; i < m; i++)
            prof[i] = rawHeight(px[i], pz[i]);
        for (let pass = 0; pass < PROFILE_BLUR_PASSES; pass++) {
            const next = Float64Array.from(prof);
            for (let i = 1; i < m - 1; i++)
                next[i] = (prof[i - 1] + prof[i] + prof[i + 1]) / 3;
            prof = next;
        }
        // The grade clamp — see MAX_PROFILE_GRADE. Fine voxels per metre of run.
        const maxRise = (i, j) => Math.hypot(px[j] - px[i], pz[j] - pz[i]) * MAX_PROFILE_GRADE / VOXEL_SCALE;
        for (let i = 1; i < m; i++) {
            const r = maxRise(i - 1, i);
            prof[i] = Math.max(prof[i - 1] - r, Math.min(prof[i - 1] + r, prof[i]));
        }
        for (let i = m - 2; i >= 0; i--) {
            const r = maxRise(i, i + 1);
            prof[i] = Math.max(prof[i + 1] - r, Math.min(prof[i + 1] + r, prof[i]));
        }
        const out = new Float64Array(m * 3);
        for (let i = 0; i < m; i++) {
            out[i * 3] = px[i];
            out[i * 3 + 1] = pz[i];
            out[i * 3 + 2] = prof[i];
        }
        return out;
    }
    /** Add a traced polyline's segments to the pool and stamp them into the presence grid. */
    register(line, material) {
        const verts = line.length / 3;
        for (let i = 0; i < verts - 1; i++) {
            if (this.n === this.ax.length)
                this.growPool();
            const k = this.n++;
            this.ax[k] = line[i * 3];
            this.az[k] = line[i * 3 + 1];
            this.ay[k] = line[i * 3 + 2];
            this.bx[k] = line[(i + 1) * 3];
            this.bz[k] = line[(i + 1) * 3 + 1];
            this.by[k] = line[(i + 1) * 3 + 2];
            this.smat[k] = material;
            // Inflated to gridReachM — the farthest any query can match AND the kernel's exact support —
            // so one read of a point's own cell stays sufficient and cell borders stay invisible.
            const r = this.gridReachM;
            const gx0 = Math.floor((Math.min(this.ax[k], this.bx[k]) - r) / GRID_M);
            const gx1 = Math.floor((Math.max(this.ax[k], this.bx[k]) + r) / GRID_M);
            const gz0 = Math.floor((Math.min(this.az[k], this.bz[k]) - r) / GRID_M);
            const gz1 = Math.floor((Math.max(this.az[k], this.bz[k]) + r) / GRID_M);
            for (let gz = gz0; gz <= gz1; gz++) {
                for (let gx = gx0; gx <= gx1; gx++) {
                    const gk = packPairKey(gx, gz);
                    let cell = this.grid.get(gk);
                    if (!cell) {
                        cell = [];
                        this.grid.set(gk, cell);
                    }
                    cell.push(k);
                }
            }
        }
    }
    /** Every traced segment, for the guards and the debug tooling. */
    forEachSegment(fn) {
        for (let k = 0; k < this.n; k++)
            fn(this.ax[k], this.az[k], this.ay[k], this.bx[k], this.bz[k], this.by[k]);
    }
    growPool() {
        const size = this.ax.length * 2;
        const grow = (old, make) => {
            const next = make(size);
            next.set(old);
            return next;
        };
        this.ax = grow(this.ax, (n) => new Float64Array(n));
        this.az = grow(this.az, (n) => new Float64Array(n));
        this.bx = grow(this.bx, (n) => new Float64Array(n));
        this.bz = grow(this.bz, (n) => new Float64Array(n));
        this.ay = grow(this.ay, (n) => new Float64Array(n));
        this.by = grow(this.by, (n) => new Float64Array(n));
        this.smat = grow(this.smat, (n) => new Int32Array(n));
    }
}
const SCRATCH = { distM: Infinity, yFine: 0, material: 0 };
function smoothstep(t) {
    const x = t < 0 ? 0 : t > 1 ? 1 : t;
    return x * x * (3 - 2 * x);
}
//# sourceMappingURL=PathNetwork.js.map